import React from 'react';
import { useQuote } from '../../contexts/QuoteContext';
import type { Model } from '../../types/quote';
import productConfig from '../../data/productConfig.json';

interface Step2ChooseModelProps {
  isActive: boolean;
  isCompleted: boolean;
  isDisabled: boolean;
}

// Helper function to get models from productConfig
const getModelsFromConfig = (productType: string): Model[] => {
  const productTypeConfig = (productConfig as any).productTypes[productType];
  if (!productTypeConfig) return [];

  return Object.values(productTypeConfig.models).map((model: any) => {
    // Get the minimum base price from all variants
    const variantPrices = Object.values(model.variants).map((variant: any) => variant.basePrice);
    const minPrice = Math.min(...variantPrices);

    return {
      id: model.id,
      name: model.name,
      description: model.description,
      basePrice: minPrice,
      imageUrl: 'https://placehold.co/200x200' // Placeholder image URL
    };
  });
};

const Step2ChooseModel: React.FC<Step2ChooseModelProps> = ({ isActive, isCompleted, isDisabled }) => {
  const { quoteState, updateQuoteState, resetSubsequentSteps } = useQuote();

  const handleModelSelect = (model: Model) => {
    if (isDisabled) return;

    // If the model is changing, reset all subsequent steps
    if (quoteState.selectedModel?.id !== model.id) {
      resetSubsequentSteps(2);
    }

    updateQuoteState({ selectedModel: model });
  };

  const getStepNumberClass = () => {
    if (isCompleted) return 'completed';
    if (isActive) return 'active';
    return 'disabled';
  };

  // Get models from productConfig based on selected product type
  const availableModels = quoteState.selectedProduct ? getModelsFromConfig(quoteState.selectedProduct) : [];

  return (
    <div className={`step-section ${isActive ? 'active' : isDisabled ? 'disabled' : 'inactive'} ${!isActive && !isCompleted ? 'collapsed' : ''}`}>
      <div className="step-header">
        <div className={`step-number ${getStepNumberClass()}`}>
          <span>2</span>
        </div>
        <h2 className="step-title">Choose Model</h2>
      </div>
      {(isActive || isCompleted) && (
        <div className="model-options">
          {availableModels.map((model) => (
            <div
              key={model.id}
              className={`model-card ${quoteState.selectedModel?.id === model.id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => handleModelSelect(model)}
              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
            >
              <div className="model-content">
                <img
                  src={model.imageUrl}
                  alt={model.name}
                  className="model-image"
                />
                <h3 className="model-name">{model.name}</h3>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Step2ChooseModel;
